import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ArrowRight, Check, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandHeader from "@/components/BrandHeader";
import BrandFooter from "@/components/BrandFooter";
import EmailSignup from "@/components/EmailSignup";

const HERO_IMAGE = "https://images.unsplash.com/photo-1593425546383-260c8b86730b?auto=format&fit=crop&w=1400&q=80";
const RECEIVE_IMAGE = "https://images.unsplash.com/photo-1500989145603-8e7ef71d639e?auto=format&fit=crop&w=1400&q=80";
const SUNRISE_IMAGE = "https://images.unsplash.com/photo-1521729839347-131a32f9abcb?auto=format&fit=crop&w=1400&q=80";

const EASE = [0.16, 1, 0.3, 1];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Breadcrumbs = () => (
  <div className="max-w-7xl mx-auto px-6 md:px-10 pt-6 flex items-center gap-2 text-xs text-[#8A8A8A]" data-testid="breadcrumbs">
    <Link to="/" className="hover:text-[#333333]">Wishing Wellness</Link>
    <ChevronRight className="h-3.5 w-3.5" />
    <span className="text-[#333333] font-semibold">The Strong Force Daily</span>
  </div>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-white" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-16 md:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-7">
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
          className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-[#333333] mb-6"
          data-testid="hero-overline"
        >
          <Zap className="h-3.5 w-3.5 text-[#E53935] fill-[#E53935]" />
          Stop Waiting. Start Becoming.
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
          className="font-display text-[2.75rem] leading-[1.02] sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A0A0A]"
          data-testid="hero-headline"
        >
          Become the Person You Keep Promising Yourself You&apos;ll Be.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18, ease: EASE }}
          className="mt-6 md:mt-8 text-lg md:text-xl text-[#4A4A4A] leading-relaxed max-w-xl"
          data-testid="hero-subheadline"
        >
          Discipline isn&apos;t built in one life-changing moment. It&apos;s built one ordinary day at a time. Start with our free 7-Day Discipline Challenge.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.28, ease: EASE }}
          className="mt-8 md:mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <Button
            onClick={() => scrollTo("start")}
            className="group rounded-full bg-[#0A0A0A] hover:bg-black text-white h-14 px-8 text-base font-semibold shadow-lg shadow-black/10 transition-all hover:-translate-y-0.5"
            data-testid="hero-primary-cta"
          >
            Download the FREE 7-Day Challenge
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
          <a
            href="#stuck"
            onClick={(e) => { e.preventDefault(); scrollTo("stuck"); }}
            className="text-sm font-medium text-[#8A8A8A] hover:text-[#333333] transition-colors"
            data-testid="hero-secondary-link"
          >
            Read why most people stay stuck →
          </a>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-6 text-sm text-[#8A8A8A]"
          data-testid="hero-social-proof"
        >
          Join thousands committed to becoming stronger every day.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
        className="lg:col-span-5 relative"
        data-testid="hero-visual"
      >
        <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)]">
          <img src={HERO_IMAGE} alt="Morning desk with journal, coffee and notebook" className="w-full h-[420px] md:h-[560px] object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/10 via-transparent to-transparent" />
        </div>
        <div className="hidden md:flex absolute -bottom-6 -left-6 items-center gap-3 bg-white rounded-2xl shadow-xl border border-[#EDE9E1] px-5 py-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0A0A0A]">
            <Zap className="h-5 w-5 text-[#E53935] fill-[#E53935]" />
          </span>
          <div>
            <div className="font-display font-bold text-sm text-[#333333]">The Strong Force Daily</div>
            <div className="text-xs text-[#8A8A8A]">Day 1 · Small actions.</div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const stuckCards = [
  { n: "01", title: "You rely on motivation.", body: "Motivation disappears." },
  { n: "02", title: "You restart every Monday.", body: "Nothing changes." },
  { n: "03", title: "You never build systems.", body: "Systems create discipline." },
];

const Stuck = () => (
  <section id="stuck" className="py-24 md:py-32 bg-white" data-testid="stuck-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: EASE }}
        className="max-w-3xl"
      >
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#E53935] mb-4">The Problem</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[1.05]">
          Why Most People Stay Stuck
        </h2>
      </motion.div>
      <div className="mt-14 md:mt-20 grid md:grid-cols-3 gap-6 md:gap-8">
        {stuckCards.map((c, i) => (
          <motion.div
            key={c.n}
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
            className="group bg-[#FAFAFA] rounded-3xl p-8 md:p-10 border border-[#EDE9E1] hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            data-testid={`stuck-card-${i + 1}`}
          >
            <div className="flex items-center justify-between mb-10">
              <span className="font-display font-extrabold text-4xl text-[#0A0A0A]/10 group-hover:text-[#0A0A0A]/20 transition-colors">{c.n}</span>
              <Zap className="h-5 w-5 text-[#E53935] fill-[#E53935]" />
            </div>
            <h3 className="font-display text-xl md:text-2xl font-bold text-[#0A0A0A] leading-snug">{c.title}</h3>
            <p className="mt-3 text-[#8A8A8A] text-base">{c.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const bullets = [
  "7 days of simple daily actions",
  "Morning mindset prompts",
  "Daily reflection",
  "Habit tracker",
  "Practical exercises",
];

const Receive = () => (
  <section id="receive" className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="receive-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: EASE }}
        className="relative order-2 lg:order-1"
      >
        <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.2)]">
          <img src={RECEIVE_IMAGE} alt="Journal and coffee on a clean desk" className="w-full h-[420px] md:h-[560px] object-cover" loading="lazy" />
        </div>
        <div className="absolute -top-6 -right-6 hidden md:block bg-white rounded-2xl shadow-xl border border-[#EDE9E1] px-5 py-4">
          <div className="text-xs text-[#8A8A8A] uppercase tracking-widest font-bold">Free Guide</div>
          <div className="font-display font-bold text-[#333333]">7-Day Challenge PDF</div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: EASE }}
        className="order-1 lg:order-2"
      >
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#E53935] mb-4">What You&apos;ll Receive</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[1.05]">
          Everything you need. Nothing you don&apos;t.
        </h2>
        <ul className="mt-10 space-y-5">
          {bullets.map((b, i) => (
            <li key={b} className="flex items-start gap-4" data-testid={`receive-bullet-${i + 1}`}>
              <span className="mt-1 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#0A0A0A]">
                <Zap className="h-3.5 w-3.5 text-[#E53935] fill-[#E53935]" />
              </span>
              <span className="text-lg text-[#0A0A0A] font-medium">{b}</span>
            </li>
          ))}
        </ul>
        <Button
          onClick={() => scrollTo("start")}
          className="mt-10 rounded-full bg-[#0A0A0A] hover:bg-black text-white h-14 px-8 text-base font-semibold shadow-lg shadow-black/10 transition-all hover:-translate-y-0.5"
          data-testid="receive-cta"
        >
          Download Free
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </motion.div>
    </div>
  </section>
);

const PromiseSection = () => (
  <section id="promise" className="py-28 md:py-40 bg-white relative" data-testid="promise-section">
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
      <motion.p
        initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="text-xs font-bold tracking-[0.3em] uppercase text-[#E53935] mb-6"
      >
        The Strong Force Promise
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
        className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] leading-[1.2] space-y-4"
      >
        <p>We don&apos;t promise overnight success.</p>
        <p>We don&apos;t promise motivation.</p>
        <p className="text-[#E53935]">We promise something better.</p>
      </motion.div>

      <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
        {["Small actions.", "Daily consistency.", "Real discipline."].map((t) => (
          <div key={t} className="rounded-2xl border border-[#EDE9E1] py-6 px-4 font-display font-bold text-lg text-[#0A0A0A]">
            {t}
          </div>
        ))}
      </div>

      <p className="mt-12 md:mt-16 font-display text-xl md:text-2xl text-[#8A8A8A] italic">
        Because ordinary days create extraordinary lives.
      </p>
    </div>
  </section>
);

const StartToday = () => (
  <section id="start" className="py-24 md:py-32 bg-white" data-testid="start-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="bg-[#FAFAFA] rounded-[2rem] md:rounded-[3rem] overflow-hidden border border-[#EDE9E1] shadow-sm">
        <div className="grid lg:grid-cols-2">
          <div className="p-8 sm:p-12 md:p-16 lg:p-20 flex flex-col justify-center">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#E53935] mb-4">Start Today</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[1.05]">
              Your first ordinary day starts now.
            </h2>
            <p className="mt-5 text-lg text-[#4A4A4A] max-w-md">
              Get the free 7-Day Discipline Challenge delivered to your inbox in the next minute.
            </p>
            <div className="mt-10">
              <EmailSignup
                source="strong_force_daily"
                testIdPrefix="signup"
                overline={null}
                headline={null}
                subtext={null}
                buttonLabel="Get My Free Challenge"
              />
            </div>
          </div>

          <div className="relative min-h-[280px] lg:min-h-full">
            <img src={SUNRISE_IMAGE} alt="Mountain sunrise" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-black/10 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-white font-display">
              <p className="text-xs font-bold tracking-[0.3em] uppercase opacity-90">Day 1</p>
              <p className="mt-2 text-2xl md:text-3xl font-bold leading-tight max-w-sm">
                Small actions. Repeated daily.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ProgramContext = () => (
  <section className="py-20 bg-[#F8F6F2]" data-testid="program-context">
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
      <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Part of Wishing Wellness</p>
      <h2 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight text-[#333333] leading-tight">
        The Strong Force Daily is the discipline pillar of a bigger vision — <span className="italic">whole-person wellness.</span>
      </h2>
      <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
        <Button asChild variant="outline" className="rounded-full bg-white border-[#EDE9E1] text-[#333333] hover:bg-[#F8F6F2] h-12 px-6 font-semibold" data-testid="program-explore-ww">
          <Link to="/">Explore Wishing Wellness →</Link>
        </Button>
        <Button asChild variant="ghost" className="rounded-full h-12 px-6 text-[#333333] hover:bg-white" data-testid="program-explore-resources">
          <Link to="/resources">Browse Resources</Link>
        </Button>
      </div>
    </div>
  </section>
);

export default function StrongForceDaily() {
  return (
    <div className="min-h-screen bg-white text-[#333333]" data-testid="tsfd-page">
      <BrandHeader variant="white" />
      <Breadcrumbs />
      <main>
        <Hero />
        <Stuck />
        <Receive />
        <PromiseSection />
        <StartToday />
        <ProgramContext />
      </main>
      <BrandFooter />
    </div>
  );
}
